import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

import { StoreClusterModel } from '@/Components/Pages/ServiceProviderPage/components';
import { createSelectors } from '@/Lib';
import { DictionaryItem } from '@/Types/api/Dictionaries';

import { StoreClusterService } from './StoreClusterService';

export enum StoreClusterMode {
    COMPANIES = 'companies',
    STORE_GROUPS = 'store_groups',
    STORES = 'stores',
}

export interface InitialClusterState {
    storeClusterName: string;
    selectedStores: DictionaryItem[];
}

interface StoreClusterStoreState {
    companies: DictionaryItem[];
    storeGroups: DictionaryItem[];
    stores: DictionaryItem[];

    selectedCompanies: DictionaryItem[];
    selectedStoreGroups: DictionaryItem[];
    selectedStores: DictionaryItem[];

    busyCompaniesIds: number[];
    busyStoreGroupsIds: number[];
    busyStoresIds: number[];

    mode: StoreClusterMode;

    storeClusterName: string;
}

interface StoreClusterStoreActions {
    setCompanies: (items: DictionaryItem[]) => void;
    setStoreGroups: (items: DictionaryItem[]) => void;
    setStores: (items: DictionaryItem[]) => void;

    setSelectedCompanies: (items: DictionaryItem[]) => void;
    setSelectedStoreGroups: (items: DictionaryItem[]) => void;
    setSelectedStores: (items: DictionaryItem[]) => void;

    setBusyIds: (companiesIds: number[], storeGroupsIds: number[], storesIds: number[]) => void;

    setSelectedDictionaryItems: (items: DictionaryItem[], mode: StoreClusterMode) => Promise<void>;
    deselectDictionaryItems: (items: DictionaryItem[], mode: StoreClusterMode) => Promise<void>;

    setStoreClusterName: (name: string) => void;

    isAllowToSaveStoreCluster: () => boolean;

    changeStoreClusterMode: (mode: StoreClusterMode) => void;

    resetState: () => void;

    getCurrentBusyCollectionIdsByMode: () => number[];

    getStoreClusterModel: () => StoreClusterModel;

    setInitialClusterState: (initialClusterState: InitialClusterState) => Promise<void>;
}

type StoreClusterStore = StoreClusterStoreState & StoreClusterStoreActions;

const getInitialState = (): StoreClusterStoreState => ({
    storeGroups: [],
    companies: [],
    stores: [],

    selectedStores: [],
    selectedStoreGroups: [],
    selectedCompanies: [],

    busyCompaniesIds: [],
    busyStoreGroupsIds: [],
    busyStoresIds: [],

    mode: StoreClusterMode.COMPANIES,

    storeClusterName: '',
});

export const storeClusterStore = create<StoreClusterStore>()(
    immer<StoreClusterStore>((set, getState, store) => ({
        ...getInitialState(),
        resetState: () => {
            set((state) => {
                const initialState = getInitialState();

                state.selectedCompanies = initialState.selectedCompanies;
                state.selectedStoreGroups = initialState.selectedStoreGroups;
                state.selectedStores = initialState.selectedStores;

                state.storeClusterName = initialState.storeClusterName;

                state.mode = initialState.mode;
            });
        },
        isAllowToSaveStoreCluster: () => {
            const { storeClusterName, selectedStores } = getState();

            const isStoreClusterNameExists = storeClusterName.trim().length > 0;
            const isSelectedStoresExists = selectedStores.length > 0;

            return isStoreClusterNameExists && isSelectedStoresExists;
        },
        setStoreClusterName: (name: string) => {
            set((state) => {
                state.storeClusterName = name;
            });
        },
        setCompanies: (items: DictionaryItem[]) => {
            set((state) => {
                state.companies = items;
            });
        },
        setStoreGroups: (items: DictionaryItem[]) => {
            set((state) => {
                state.storeGroups = items;
            });
        },
        setStores: (items: DictionaryItem[]) => {
            set((state) => {
                state.stores = items;
            });
        },
        setSelectedCompanies: (companies: DictionaryItem[]) => {
            set((state) => {
                state.selectedCompanies = companies;
            });
        },
        setSelectedStoreGroups: (storeGroups: DictionaryItem[]) => {
            set((state) => {
                state.selectedStoreGroups = storeGroups;
            });
        },
        setSelectedStores: (stores: DictionaryItem[]) => {
            set((state) => {
                state.selectedStores = stores;
            });
        },
        setSelectedDictionaryItems: async (items: DictionaryItem[], mode: StoreClusterMode) => {
            const state = getState();

            const storeClusterService = new StoreClusterService();

            switch (mode) {
                case StoreClusterMode.COMPANIES: {
                    const companies = [...state.selectedCompanies, ...items];

                    const matchedStoresByCompanies = await storeClusterService.getStoresByCompanies(
                        companies.map((company) => company.id),
                        state.stores,
                        state.selectedStores
                    );

                    state.setSelectedStores([...state.selectedStores, ...matchedStoresByCompanies]);
                    state.setSelectedCompanies(companies);

                    break;
                }
                case StoreClusterMode.STORE_GROUPS: {
                    const storeGroups = [...state.selectedStoreGroups, ...items];

                    const matchedStoresByStoreGroups = await storeClusterService.getStoresByStoreGroups(
                        storeGroups.map((storeGroup) => storeGroup.id),
                        state.stores,
                        state.selectedStores
                    );

                    state.setSelectedStores([...state.selectedStores, ...matchedStoresByStoreGroups]);
                    state.setSelectedStoreGroups(storeGroups);
                    break;
                }
                case StoreClusterMode.STORES: {
                    const stores = [...state.selectedStores, ...items];

                    state.setSelectedStores(stores);
                    break;
                }
            }
        },
        deselectDictionaryItems: async (dictionaryCollection: DictionaryItem[], mode: StoreClusterMode) => {
            const state = getState();

            const storeClusterService = new StoreClusterService();

            const idsCollection = dictionaryCollection.map((item) => item.id);

            switch (mode) {
                case StoreClusterMode.COMPANIES: {
                    const stores = await storeClusterService.getStoresByCompanies(idsCollection, state.stores, []);

                    state.setSelectedStores(stores);
                    state.setSelectedCompanies(dictionaryCollection);
                    break;
                }
                case StoreClusterMode.STORE_GROUPS: {
                    const stores = await storeClusterService.getStoresByStoreGroups(idsCollection, state.stores, []);

                    state.setSelectedStores(stores);
                    state.setSelectedStoreGroups(dictionaryCollection);
                    break;
                }
                case StoreClusterMode.STORES: {
                    state.setSelectedStores(dictionaryCollection);
                    break;
                }
            }
        },
        changeStoreClusterMode: (nextMode: StoreClusterMode) => {
            set((state) => {
                state.mode = nextMode;
            });
        },
        setBusyIds: (companiesIds: number[], storeGroupsIds: number[], storesIds: number[]) => {
            set((state) => {
                state.busyCompaniesIds = companiesIds;
                state.busyStoreGroupsIds = storeGroupsIds;
                state.busyStoresIds = storesIds;
            });
        },
        getCurrentBusyCollectionIdsByMode: (): number[] => {
            const state = getState();

            switch (state.mode) {
                case StoreClusterMode.COMPANIES:
                    return state.busyCompaniesIds;
                case StoreClusterMode.STORE_GROUPS:
                    return state.busyStoreGroupsIds;
                case StoreClusterMode.STORES:
                    return state.busyStoresIds;
            }
        },
        getStoreClusterModel: () => {
            const state = getState();

            return {
                name: state.storeClusterName,
                stores: state.selectedStores.map((store) => store.id),
            };
        },
        setInitialClusterState: async (initialClusterState: InitialClusterState) => {
            set((state) => {
                state.storeClusterName = initialClusterState.storeClusterName;
                state.selectedStores = initialClusterState.selectedStores;
            });

            const storeIds = initialClusterState.selectedStores.map((store) => store.id);

            if (storeIds.length === 0) {
                return;
            }

            const storeClusterService = new StoreClusterService();

            const [companies, storeGroups] = await Promise.all([
                storeClusterService.getCompaniesByStores(storeIds),
                storeClusterService.getStoreGroupsByStores(storeIds),
            ]);

            set((state) => {
                state.selectedCompanies = companies;
                state.selectedStoreGroups = storeGroups;
            });
        },
    }))
);

export const storeClusterStoreSelectors = createSelectors(storeClusterStore);
